import React, { useEffect, useState } from "react"; // Add useEffect to the imports
import {
  Modal,
  Dropdown,
  Space,
  Typography,
  Button,
  Form,
  Input,
  message,
  DatePicker,
  Upload,
} from "antd";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import dayjs from "dayjs";
import { Alert, Flex, Spin } from "antd";
import S3UploadButton from "./S3UploadButton";
const ProfilePopup = ({ isOpen, closeModal, data }) => {
  const API_URL = import.meta.env.VITE_API_URL;
  const accessToken = useSelector((state) => state.user.accessToken);
  const [formDisabled, setFormDisabled] = useState(true);
  const user = useSelector((state) => state.user.user);
  const [form] = Form.useForm();
  const [userData, setUserData] = useState(null);
  const userId = data ? data : user;
  const contentStyle = {
    padding: 50,
    background: "rgba(0, 0, 0, 0.05)",
    borderRadius: 4,
  };

  // Add responsive styling
  const modalResponsiveStyle = {
    modalWidth: {
      xs: '95vw',
      sm: '90vw',
      md: '85vw',
      lg: '75vw',
      xl: '65vw',
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}users?id=${userId.id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.data) {
        setUserData(response.data.user);
      } else {
        console.warn("API response does not contain userDetails");
        setUserData(null); // Ensure safe handling
      }
    } catch (error) {
      console.error("API Fetch Error:", error);
      message.error("Failed to fetch user data.");
      setUserData(null);
    }
  };

  useEffect(() => {
    if (!userId?.id) {
      // console.log("Skipping API call: userId.id is null or undefined");
      return;
    }
    fetchData();
  }, [userId?.id, API_URL, accessToken]);

  // Update form values dynamically when userData.branches changes
  useEffect(() => {
    if (userData && isOpen) {
      const initialValues = {
        yearsOfExperience: userData?.userDetails?.yearsOfExperience || "",
        licenseImage: userData?.userDetails?.licenseImage || "",
        Date_of_birth: userData?.userDetails?.dob
          ? moment(userData.userDetails.dob)
          : null,
        licenseExpiryDate: userData?.userDetails?.licenceExpiryDate
          ? moment(userData.userDetails.licenceExpiryDate)
          : null,
        employmentStartDate: userData?.userDetails?.employmentStartDate
          ? moment(userData.userDetails.employmentStartDate)
          : null,
      };

      if (userData.roleDetails?.id === 1) {
        initialValues.Height = userData?.userDetails?.Height || "";
        initialValues.Weight = userData?.userDetails?.Weight || "";
        initialValues.Position = userData?.userDetails?.position || "";
      } else {
        if (userData.roleDetails?.id === 4) {
          initialValues.pharmacy = userData?.pharmacyDetails?.name || "";
        } else {
          initialValues.pharmacy =
            userData?.branches?.[0]?.PharmacyDetails?.name || "";
          initialValues.branch = userData?.branches?.[0]?.name || "";
        }
        initialValues.position = userData?.userDetails?.position || "";
      }

      form.setFieldsValue(initialValues);
    }
  }, [userData, form, isOpen]);

  const handleSave = async () => {
    try {
      const formValues = await form.validateFields();

      // Get original values from userData
      const originalValues = {
        dob: userData?.userDetails?.dob || null,
        yearsOfExperience: userData?.userDetails?.yearsOfExperience || "",
        licenseImage: userData?.userDetails?.licenseImage || "",
        licenceExpiryDate: userData?.userDetails?.licenceExpiryDate || null,
        employmentStartDate: userData?.userDetails?.employmentStartDate || null,
        ...(userData.roleDetails?.id === 1
          ? {
            Height: userData?.userDetails?.Height || "",
            Weight: userData?.userDetails?.Weight || "",
            Position: userData?.userDetails?.position || "",
          }
          : {
            position: userData?.userDetails?.position || "",
          }),
      };
      // Prepare updated data
      const updatedData = {};
      console.log(formValues.licenseImage !== originalValues.licenseImage);
      // Handle Date of Birth
      if (
        formValues.Date_of_birth &&
        !moment(formValues.Date_of_birth).isSame(
          moment(originalValues.dob),
          "day"
        )
      ) {
        updatedData.dob = formValues.Date_of_birth.format("YYYY-MM-DD");
      }

      // Handle Years of Experience
      if (
        formValues.yearsOfExperience !== undefined &&
        formValues.yearsOfExperience !== originalValues.yearsOfExperience
      ) {
        updatedData.yearsOfExperience = formValues.yearsOfExperience;
      }
      if (formValues.licenseImage !== originalValues.licenseImage) {
        if (formValues.licenseImage == null) {
          // console.log(formValues.licenseImage)
          return (updatedData.licenseImage = null);
        }
        updatedData.licenseImage = formValues.licenseImage;
      }

      // Handle License Expiry Date and Employment Start Date together
      if (formValues.licenseExpiryDate) {
        const licenseExpiryDate = dayjs(formValues.licenseExpiryDate); // Ensure it's dayjs
        const employmentStartDate = dayjs(
          formValues.employmentStartDate || originalValues.employmentStartDate
        );

        if (!employmentStartDate.isValid()) {
          message.error("Employment Start Date is required.");
          return;
        }

        if (licenseExpiryDate.isBefore(employmentStartDate, "day")) {
          message.error(
            "License Expiry Date cannot be earlier than Employment Start Date."
          );
          return;
        }

        // Only add if dates are different from original
        if (
          !licenseExpiryDate.isSame(
            dayjs(originalValues.licenceExpiryDate),
            "day"
          )
        ) {
          updatedData.licenceExpiryDate =
            licenseExpiryDate.format("YYYY-MM-DD");
          updatedData.employmentStartDate =
            employmentStartDate.format("YYYY-MM-DD");
        }
      }

      // Handle role-specific fields
      if (userData.roleDetails?.id === 1) {
        if (
          formValues.Height !== undefined &&
          formValues.Height !== originalValues.Height
        ) {
          updatedData.Height = formValues.Height;
        }
        if (
          formValues.Weight !== undefined &&
          formValues.Weight !== originalValues.Weight
        ) {
          updatedData.Weight = formValues.Weight;
        }
        if (
          formValues.Position !== undefined &&
          formValues.Position !== originalValues.Position
        ) {
          updatedData.position = formValues.Position;
        }
      } else {
        if (
          formValues.position !== undefined &&
          formValues.position !== originalValues.position
        ) {
          updatedData.position = formValues.position;
        }
      }

      // Check if there are any changes
      if (Object.keys(updatedData).length === 0) {
        message.warning("No changes detected.");
        setFormDisabled(true);
        return;
      }

      const response = await axios.patch(
        `${API_URL}UserProfiles/?id=${userData.id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.status === 200) {
        message.success("User updated successfully");
        setFormDisabled(true);
        setUserData((prev) => ({
          ...prev,
          userDetails: { ...prev.userDetails, ...updatedData },
        }));
        fetchData(); // Refresh the data
      }
    } catch (error) {
      console.error("Error while saving:", error);
      message.error(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    closeModal();
    setFormDisabled(true);
  };
  return (
    <div className="modal-wrapper">
      <Modal
        open={isOpen}
        onCancel={handleCancel}
        width={modalResponsiveStyle.modalWidth}
        centered
        className="profilePopup"
        footer={null}
      >
        {userData ? (
          <>
            <div className="modal-inner flex flex-col md:flex-row justify-between bg-[#F8F8F8] shadow-none rounded-none p-0">
              <div className="user-info bg-white rounded-lg w-full md:w-[34%] flex flex-col items-center gap-y-4 p-5 justify-center mb-4 md:mb-0">
                <img
                  src={
                    userData?.userDetails?.profilePicture ||
                    "/pharmacist-pic.jpg"
                  }
                  alt="User Picture"
                  className="h-[120px] w-[120px] rounded-[50%] object-cover"
                />
                <div className="personal-info flex flex-col items-center gap-y-2">
                  <h3 className="name font-[#232323] font-semibold text-[20px] md:text-[24px] text-center">
                    {userData.userDetails
                      ? userData.userDetails.firstName +
                      " " +
                      userData.userDetails.lastName
                      : "No Name Available"}
                  </h3>
                  <p className="gender">
                    {userData.userDetails
                      ? userData.userDetails.gender || "Not Specified"
                      : "Null"}
                  </p>
                  <p className="mobile-number">
                    {userData.userDetails
                      ? userData.mobileNumber
                      : "No Mobile Number "}
                  </p>
                </div>
              </div>
              <div className="user-fields bg-white rounded-lg w-full md:w-[64%] flex flex-col p-4 md:p-5">
                <div className="top flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
                  <div className="activity-status flex gap-x-2 w-fit">
                    <p className="font-medium text-[#232323] my-auto">
                      Activity Status
                    </p>
                    <Space
                      className={`${userData?.active === true
                        ? "bg-[#E8FAE9] text-[#14CC26] py-2 px-3 font-medium"
                        : "bg-red-200 text-red-700 py-2 px-3 font-medium"
                        } ${"rounded"}`}
                    >
                      {userData?.active ? "Active" : "Inactive"}
                    </Space>
                  </div>
                  <div>
                    {formDisabled ? (
                      <Button
                        className="fv-button"
                        size="middle"
                        onClick={() => setFormDisabled(false)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        className="bg-[#3371eb] text-white"
                        size="middle"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                </div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSave}
                  className="flex flex-row flex-wrap justify-between gap-y-[20px] w-100"
                >
                  {userData.roleDetails?.id == 1 ? (
                    <>
                      <Form.Item
                        name="Height"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Height
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <Input
                          disabled={formDisabled}
                          style={{ width: "100%", height: "40px" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="Weight"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Weight
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <Input
                          disabled={formDisabled}
                          style={{ width: "100%", height: "40px" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="Position"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Position
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <Input
                          disabled={formDisabled}
                          style={{ width: "100%", height: "40px" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="yearsOfExperience"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Years of Experience
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <Input
                          disabled={formDisabled}
                          style={{ width: "100%", height: "40px" }}
                          rules={[
                            {
                              pattern: new RegExp(/^[0-9]*$/),
                              message: "Please enter numbers only",
                            },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Date of birth
                          </p>
                        }
                        name="Date_of_birth"
                      >
                        <DatePicker
                          disabled={formDisabled}
                          prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                          suffixIcon={null}
                          placeholder=""
                          style={{ width: "100%", height: "40px" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="licenseImage"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            License Photo
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <S3UploadButton
                          style={{
                            height: "40px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "start",
                            columnGap: "10px",
                            padding: "0 15px",
                            alignItems: "center",
                          }}
                          name="Upload License Photo"
                          disabled={formDisabled}
                          maxUploads={1}
                          onChange={(url) => {
                            form.setFieldsValue({ licenseImage: url });
                          }}
                          initValue={userData?.userDetails?.licenseImage || ""}
                        />
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      {userData.roleDetails?.id === 4 ? (
                        <Form.Item
                          name="pharmacy"
                          label={
                            <p
                              style={{
                                color: "#232323",
                                fontWeight: 500,
                                fontSize: "16px",
                              }}
                            >
                              Pharmacy
                            </p>
                          }
                          className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                        >
                          <Input
                            value={
                              userData?.pharmacyDetails?.name ||
                              "No pharmacy data"
                            }
                            disabled
                            style={{ width: "100%", height: "40px" }}
                          />
                        </Form.Item>
                      ) : (
                        <>
                          <Form.Item
                            name="pharmacy"
                            label={
                              <p
                                style={{
                                  color: "#232323",
                                  fontWeight: 500,
                                  fontSize: "16px",
                                }}
                              >
                                Pharmacy
                              </p>
                            }
                            className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                          >
                            {userData?.branches?.length > 0 ? (
                              userData.branches.map((branch) => (
                                <Input
                                  key={branch.id}
                                  value={
                                    branch.PharmacyDetails?.name ||
                                    "No pharmacy name"
                                  }
                                  style={{
                                    width: "100%",
                                    height: "40px",
                                    marginBottom: "8px",
                                  }}
                                  disabled
                                />
                              ))
                            ) : (
                              <Input
                                value="No pharmacy data"
                                style={{ width: "100%", height: "40px" }}
                                disabled
                              />
                            )}
                          </Form.Item>
                          <Form.Item
                            name="branch"
                            label={
                              <p
                                style={{
                                  color: "#232323",
                                  fontWeight: 500,
                                  fontSize: "16px",
                                }}
                              >
                                Branch
                              </p>
                            }
                            className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                          >
                            {userData?.branches?.length > 0 ? (
                              userData.branches.map((branch) => (
                                <Input
                                  key={branch.id}
                                  value={branch.name || "No branch name"}
                                  style={{
                                    width: "100%",
                                    height: "40px",
                                    marginBottom: "8px",
                                  }}
                                  disabled
                                />
                              ))
                            ) : (
                              <Input
                                value="No branch data"
                                style={{ width: "100%", height: "40px" }}
                                disabled
                              />
                            )}
                          </Form.Item>
                        </>
                      )}

                      <Form.Item
                        name="position"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Position
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <Input
                          disabled={formDisabled}
                          style={{ width: "100%", height: "40px" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name="yearsOfExperience"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Years of Experience
                          </p>
                        }
                        className={`m-0 h-fit ${userData.roleDetails?.id === 4
                          ? "w-full"
                          : "w-full sm:w-[calc(50%-10px)]"
                          }`}
                      >
                        <Input
                          disabled={formDisabled}
                          type="number"
                          style={{ width: "100%", height: "40px" }}
                          min={0}
                        />
                      </Form.Item>
                      <Form.Item
                        name="employmentStartDate"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Employment Start Date
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <DatePicker
                          prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                          suffixIcon={null}
                          placeholder=""
                          style={{ width: "100%", height: "40px" }}
                          disabled={true}
                        />
                      </Form.Item>
                      <Form.Item
                        name="licenseExpiryDate"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            License Expiry Date
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <DatePicker
                          prefix={
                            <div onMouseDown={(e) => e.stopPropagation()}>
                              <img src="/assets/images/date.svg" alt="calendar icon" />
                            </div>
                          }
                          suffixIcon={null}
                          {...(!formDisabled && {
                            onOpenChange: (open) => {
                              if (open) {
                                form.setFieldsValue({
                                  licenseExpiryDate: null,
                                });
                              }
                            },
                          })}
                          onChange={(date) => {
                            if (date) {
                              form.setFieldsValue({ licenseExpiryDate: date });
                            }
                          }}
                          placeholder=""
                          style={{ width: "100%", height: "40px" }}
                          disabled={formDisabled}
                        />
                      </Form.Item>
                      <Form.Item
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            Date of Birth
                          </p>
                        }
                        name="Date_of_birth"
                      >
                        <DatePicker
                          disabled={formDisabled}
                          prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                          suffixIcon={null}
                          placeholder=""
                          onOpenChange={(open) => {
                            if (open) {
                              form.setFieldsValue({ Date_of_birth: null });
                            }
                          }}
                          style={{ width: "100%", height: "40px" }}
                          disabledDate={(current) =>
                            current && current > moment().endOf("day")
                          }
                        />
                      </Form.Item>

                      <Form.Item
                        name="licenseImage"
                        label={
                          <p
                            style={{
                              color: "#232323",
                              fontWeight: 500,
                              fontSize: "16px",
                            }}
                          >
                            License Photo
                          </p>
                        }
                        className="w-full sm:w-[calc(50%-10px)] m-0 h-fit"
                      >
                        <S3UploadButton
                          style={{
                            height: "40px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "start",
                            columnGap: "10px",
                            padding: "0 15px",
                            alignItems: "center",
                          }}
                          name="Upload License Photo"
                          disabled={formDisabled}
                          maxUploads={1}
                          onChange={(url) => {
                            if (url) {
                              form.setFieldsValue({ prescriptionImage: url });
                            } else {
                              form.setFieldsValue({ prescriptionImage: null });
                            }
                          }}
                          initValue={userData?.userDetails?.licenseImage || ""}
                        />
                      </Form.Item>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </>
        ) : (
          <Spin tip="Loading" size="large">
            <div className="content-loading-container" style={{ padding: "50px" }}></div>
          </Spin>
        )}
      </Modal>
    </div>
  );
};

export default ProfilePopup;
